import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppConstants } from '../app.constants';

/**
 * Client for the Spark Bone hunt admin API (GoFloatersAPI3/functions/sparkhunt,
 * reference in GoFloatersAPI3/scripts/spark-hunt/ADMIN-API.md).
 *
 * The API runs in the same Firebase project the admin app signs in to, so the
 * ID token TokenInterceptor attaches is valid there. Access also needs the
 * user's email on the hunt-admin list (CLI: hunt-admin.js admins --add).
 */
export const HUNT_API_BASE = (() => {
  const env = AppConstants.CONSTANTS[AppConstants.CONSTANTS.currentEnvironment];
  return `https://us-central1-${env.firebase.projectId}.cloudfunctions.net/sparkhunt/admin`;
})();

export type HuntStatus = 'running' | 'paused' | 'stopped';
export type BoneStatus = 'unscheduled' | 'scheduled' | 'live' | 'found' | 'missed';

export interface HuntState { status: HuntStatus; pausedAt: number | null; changedAt: number | null; changedBy: string | null; }
export interface Clue { after: number; text: string; }
export interface Room { spaceId: string; spaceName: string; building: string | null; locality: string | null; city: string | null; photoUrl: string | null; }
export interface ClaimInfo { code: string; at: number; checks?: number; secondsToFind?: number; roomsPerMinute?: number; flagged?: boolean; }
export interface Bone {
  id: string; n: number; status: BoneStatus; opensAt: number | null; closesAt: number | null;
  clues: Clue[]; reveal: string | null; room: Room | null; claim: ClaimInfo | null; updatedAt: number | null;
}
export interface Overview {
  state: HuntState; goal: number;
  bones: { total: number; found: number; live: number; scheduled: number; unscheduled: number; missed: number };
  foundCounter: number; live: Bone | null; next: Bone[]; recentFinds: Bone[]; flaggedFinds: number;
  players: { onlineNow: number; explorersToday: number };
}
export interface ImportBone { n: number; id: string; spaceId: string; room: string; building: string | null; locality: string | null; city: string | null; clues: Clue[]; reveal: string | null; replaces: boolean; }
export interface ImportResult {
  dryRun: boolean; saved: number; bones: ImportBone[];
  warnings: { line: number; bone: number; spaceId: string; warning: string }[];
  errors: { line: number; bone: number; spaceId: string; error: string }[];
}
export interface ScheduleRequest {
  from: string; every?: number; window?: string; times?: string[]; duration: number;
  days?: string[]; bones?: number[]; dryRun?: boolean;
}
export interface ScheduleResult { dryRun: boolean; scheduled: { n: number; opensAt: number; closesAt: number }[]; }
export interface Claim { code: string; n: number; at: number; voided: boolean; voidReason: string | null; checks?: number; secondsToFind?: number; roomsPerMinute?: number; flagged?: boolean; }
export interface Verification { valid: boolean; reason: string | null; n?: number; at?: number; checks?: number; secondsToFind?: number; roomsPerMinute?: number; flagged?: boolean; }
export interface AuditEntry { id: string; at: number; by: string; action: string; details: any; }

@Injectable({ providedIn: 'root' })
export class HuntApiService {
  readonly base = HUNT_API_BASE;

  constructor(private http: HttpClient) {}

  // Dashboard + state
  me(): Observable<{ email: string }> { return this.get('/me'); }
  overview(): Observable<Overview> { return this.get('/overview'); }
  setState(action: 'start' | 'pause' | 'resume' | 'stop', shift = true): Observable<HuntState & { shiftedBones: number }> {
    return this.post('/state', { action, shift });
  }

  // Settings
  settings(): Observable<{ settings: any; defaults: any }> { return this.get('/settings'); }
  updateSettings(changes: any): Observable<any> { return this.wrap(this.http.patch(this.base + '/settings', changes)); }

  // Bones
  bones(status?: BoneStatus): Observable<{ bones: Bone[] }> { return this.get('/bones' + (status ? `?status=${status}` : '')); }
  bone(n: number): Observable<Bone> { return this.get(`/bones/${n}`); }
  updateBone(n: number, changes: { clues?: Clue[]; reveal?: string | null; spaceId?: string }): Observable<Bone> {
    return this.wrap(this.http.patch<Bone>(`${this.base}/bones/${n}`, changes));
  }
  deleteBone(n: number): Observable<any> { return this.wrap(this.http.delete(`${this.base}/bones/${n}`)); }
  importCsv(csv: string, opts: { dryRun: boolean; force?: boolean; startAt?: number }): Observable<ImportResult> {
    return this.post('/bones/import', { csv, ...opts });
  }

  // Schedule
  schedule(req: ScheduleRequest): Observable<ScheduleResult> { return this.post('/schedule', req); }
  unschedule(bones?: number[]): Observable<{ unscheduled: number }> { return this.post('/unschedule', { bones }); }

  // Claims
  claims(): Observable<{ claims: Claim[] }> { return this.get('/claims'); }
  verify(code: string): Observable<Verification> { return this.get(`/claims/${encodeURIComponent(code.trim().toUpperCase())}`); }
  voidClaim(code: string, reason: string): Observable<{ voided: string; n: number }> {
    return this.post(`/claims/${encodeURIComponent(code)}/void`, { reason });
  }

  // Reset
  reset(scope: 'found' | 'everything', unschedule: boolean): Observable<any> {
    return this.post('/reset', { scope, unschedule, confirm: 'RESET' });
  }

  // Admins + audit
  admins(): Observable<{ admins: string[] }> { return this.get('/admins'); }
  addAdmin(email: string): Observable<{ admins: string[] }> { return this.post('/admins', { email }); }
  removeAdmin(email: string): Observable<{ admins: string[] }> {
    return this.wrap(this.http.delete<{ admins: string[] }>(`${this.base}/admins/${encodeURIComponent(email)}`));
  }
  audit(limit = 100): Observable<{ entries: AuditEntry[] }> { return this.get(`/audit?limit=${limit}`); }

  private get<T>(path: string): Observable<T> { return this.wrap(this.http.get<T>(this.base + path)); }
  private post<T>(path: string, body: any): Observable<T> { return this.wrap(this.http.post<T>(this.base + path, body)); }

  /** Turns API errors into { status, message } with the server's message. */
  private wrap<T>(obs: Observable<T>): Observable<T> {
    return obs.pipe(catchError((e: HttpErrorResponse) => throwError({
      status: e.status,
      message: (e.error && e.error.error) || (e.status === 0 ? "Couldn't reach the hunt API" : `Request failed (${e.status})`),
    })));
  }
}
